import type { ReactNode } from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "../../../../ui/index.ts";
import type { ProjectWithAllocationsDerived } from "../../../../../hooks/time-allocation/use-time-allocation-page.ts";
import type { AllocationPeriodUi } from "../../../../../types/time-allocation.ts";
import { formatAllocationCurrentLabel } from "../../../../../utils/allocation-period-label.ts";
import { AllocationPeriodToggle } from "../allocation-period-toggle/allocation-period-toggle.tsx";

import styles from "./project-accordion.module.css";

export type ProjectAccordionProps = {
  projects: ProjectWithAllocationsDerived[];
  /** Controlled open item id, or null */
  openValue: string | null;
  onOpenChange: (value: string | null) => void;
  /** Current period per project (overrides `project.period` when set) */
  periodByProjectId: Record<string, AllocationPeriodUi>;
  /** Fired when user picks a different period segment (parent may show confirm first) */
  onPeriodChangeRequest: (projectId: string, next: AllocationPeriodUi) => void;
  /**
   * Toolbar rendered inside the accordion header when this item is open (e.g. Save/Discard).
   */
  renderHeaderToolbar?: (
    project: ProjectWithAllocationsDerived,
  ) => ReactNode;
  /** Expanded panel body per project (table region only; scrolls with the page) */
  renderProjectContent: (project: ProjectWithAllocationsDerived) => ReactNode;
};

export function ProjectAccordion({
  projects,
  openValue,
  onOpenChange,
  periodByProjectId,
  onPeriodChangeRequest,
  renderHeaderToolbar,
  renderProjectContent,
}: ProjectAccordionProps) {
  return (
    <Accordion
      type="single"
      value={openValue}
      onValueChange={onOpenChange}
      collapsible
      className={styles.accordion}
    >
      {projects.map((project) => {
        const labelId = `proj-label-${project.id}`;
        const currentPeriod: AllocationPeriodUi =
          periodByProjectId[project.id] ?? project.period;

        const currentLabel =
          project.employees.length === 0
            ? null
            : formatAllocationCurrentLabel(
                project.apiPeriod,
                project.allocationDate,
                project.customStartDate,
                project.customEndDate,
              );

        const headerToolbarNode =
          openValue === project.id && renderHeaderToolbar != null
            ? renderHeaderToolbar(project)
            : null;

        return (
          <AccordionItem
            key={project.id}
            value={project.id}
            className={styles.accordionItemOverflow}
          >
            <AccordionHeader className={styles.itemHeader}>
              <span
                id={labelId}
                className={clsx("sub-heading-md", styles.projectName)}
              >
                {project.name}
              </span>
              <AllocationPeriodToggle
                ariaLabelledBy={labelId}
                currentLabel={currentLabel}
                value={currentPeriod}
                onChange={(next) => {
                  if (next !== currentPeriod) {
                    onPeriodChangeRequest(project.id, next);
                  }
                }}
              />
              {headerToolbarNode != null && (
                <div className={styles.headerToolbarSlot}>
                  {headerToolbarNode}
                </div>
              )}
              <AccordionTrigger
                className={styles.iconTrigger}
                aria-label={`Expand or collapse ${project.name}`}
              >
                <ChevronDown
                  className={styles.chevron}
                  size={20}
                  strokeWidth={2}
                  aria-hidden
                />
              </AccordionTrigger>
            </AccordionHeader>
            <AccordionContent
              className={clsx(styles.itemContent, styles.itemContentTight)}
            >
              {renderProjectContent(project)}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
